import { ApexOptions } from "apexcharts";
import React from "react";
import ReactApexChart from "react-apexcharts";
import { Button, Checkbox, CheckboxGroup, CheckPicker, FormGroup, Notification, Radio, RadioGroup } from "rsuite";
import axios from "../../../../utilities/axios";
import styles from "./styles/lineChartPerWeek.module.scss";
import { CheckPickerSelectFilter } from "../../../../components/formfields/CheckPickerSelectFilter";
import handleGenerateHarmoniousColors from "../../utils/handleGenerateRandomColor";
import dayjs from "dayjs";

const lineChartFilterOptions: string[] = ['1', '2', '3', '4']

interface LineChartPerWeekProps {
    stores: { label: string; value: number }[];
    platforms: { label: string; value: number }[];
    errors: { label: string; value: number }[];
}

interface LineChartPerWeekState {
    series: {
        name: string;
        data: number[];
    }[];
    options: ApexOptions;
    filters: {
        weekSelect: {
            indeterminate: boolean,
            checkAll: boolean,
            value: string[],
        },
        employee: number[],
        store: number[],
        platform: number[],
        lineChartFilterType: string,
        errors: number[],
    },
    loadingButton: boolean;
}

class LineChartPerWeek extends React.Component<LineChartPerWeekProps, LineChartPerWeekState> {
    public state: LineChartPerWeekState = {
        series: [],
        options: {
            chart: {
                height: 350,
                type: 'line',
                zoom: {
                    enabled: false
                }
            },
            colors: [],
            dataLabels: {
                enabled: false
            },
            stroke: {
                curve: 'straight'
            },
            grid: {
                row: {
                    colors: ['#f3f3f3', 'transparent'],
                    opacity: 0.5
                },
            },
            xaxis: {
                categories: ['Semana 1', 'Semana 2', 'Semana 3', 'Semana 4'],
            },
        },
        filters: {
            weekSelect: {
                indeterminate: true,
                checkAll: true,
                value: ['1', '2', '3', '4'],
            },
            employee: [],
            store: [],
            platform: [],
            lineChartFilterType: "",
            errors: [],
        },
        loadingButton: false
    }

    async componentDidMount() {
        await this.handleGetChartData;
    }

    handleGetChartData = async () => {
        this.setState({
            loadingButton: true
        })

        try {
            const { filters } = this.state;

            if (!filters?.lineChartFilterType) {
                Notification.warning({
                    title: "Aviso!",
                    description: "Selecione Loja ou Plataforma para buscar as informações",
                    duration: 3500
                })

                this.setState({
                    loadingButton: false
                })

                return;
            }

            if (filters.weekSelect?.value.length === 0) {
                Notification.warning({
                    title: "Aviso!",
                    description: "Selecione o período para fazer a busca",
                    duration: 3500
                })

                this.setState({
                    loadingButton: false
                })

                return;
            }

            const sortedWeekSelect = filters.weekSelect?.value.sort((a: any, b: any) => Number(a) - Number(b))

            const lineChartResponse = await axios.get("/api/v1/dashboard/get_linechart_information", {
                params: {
                    weekSelect: sortedWeekSelect ? sortedWeekSelect : [],
                    lineChartFilterType: filters?.lineChartFilterType ? filters?.lineChartFilterType : "",
                    employee: filters?.employee ? filters?.employee : [],
                    store: filters?.store ? filters?.store : [],
                    platform: filters?.platform ? filters?.platform : [],
                    errors: filters?.errors ? filters?.errors : [],
                }
            })

            const { series, categories } = lineChartResponse.data;

            this.setState({
                series: series,
                options: {
                    ...this.state.options,
                    colors: handleGenerateHarmoniousColors(series.length),
                    xaxis: {
                        categories: categories,
                    },
                }
            })

        } catch (error) {
            Notification.error({
                title: "Erro!",
                description: "Erro ao buscar informação para o gráfico de linha",
                duration: 3500
            })
            console.log("Erro ao pegar dados para o gráfico de linha", error)
        }

        this.setState({
            loadingButton: false
        })
    }


    handleCheckAll = (value: any, checked: any) => {
        const nextValue = checked ? lineChartFilterOptions : [];
        this.setState((prevState) => ({
            filters: {
                ...prevState.filters,
                weekSelect: {
                    value: nextValue,
                    indeterminate: false,
                    checkAll: checked,
                },
            },
        }));
    };

    handleChange = (value: any) => {
        this.setState((prevState) => ({
            filters: {
                ...prevState.filters,
                weekSelect: {
                    value,
                    indeterminate: value.length > 0 && value.length < lineChartFilterOptions.length,
                    checkAll: value.length === lineChartFilterOptions.length,
                },
            },
        }));
    };

    render() {
        const renderWeeks = () => {
            const today = dayjs()
            const currentMonday = today.clone().subtract(today.clone().day() == 0 ? 1 : 0, 'day').subtract(1, 'week').startOf('week').add(1, 'day');

            return [1, 2, 3, 4].map(week => (
                <Checkbox value={week.toString()}>
                    {currentMonday.clone().subtract(7 * (week - 1), 'day').startOf('day').format('DD/MM')}
                    &nbsp;-&nbsp;
                    {currentMonday.clone().subtract(7 * (week - 1), 'day').add(6, 'day').endOf('day').format('DD/MM')}
                </Checkbox>
            ))
        }

        return (
            <>
                <div className={styles.lineChart}>
                    <div className={styles.lineChartFilters}>
                        <div className={styles.fieldsFilterDivider}>
                            <FormGroup controlId="radioList">
                                <span>‎ </span>
                                <RadioGroup
                                    name="radioList"
                                    inline
                                    style={{ display: 'flex' }}
                                    value={this.state.filters.lineChartFilterType}
                                    onChange={(value) =>
                                        this.setState((prevState) => ({
                                            filters: {
                                                ...prevState.filters,
                                                lineChartFilterType: value,
                                            },
                                        }))
                                    }>
                                    <Radio value="store">Loja</Radio>
                                    <Radio value="platform">Plataforma</Radio>
                                </RadioGroup>
                            </FormGroup>

                            {this.state.filters.lineChartFilterType === 'store' && (
                                <div className={styles.filterDiv} style={{ width: '100%' }}>
                                    <span>Lojas</span>
                                    <CheckPicker
                                        data={this.props.stores}
                                        placeholder="Lojas para filtro..."
                                        value={this.state.filters.store}
                                        style={{ width: '100%' }}
                                        onChange={(value) =>
                                            this.setState((prevState) => ({
                                                filters: {
                                                    ...prevState.filters,
                                                    store: value,
                                                },
                                            }))
                                        }
                                    />
                                    <span className={styles.informationText}>
                                        *Se nenhum filtro for aplicado, todos os registros serão incluídos
                                    </span>
                                </div>
                            )}

                            {this.state.filters.lineChartFilterType === 'platform' && (
                                <div className={styles.filterDiv} style={{ width: '100%' }}>
                                    <span>Plataformas</span>
                                    <CheckPicker
                                        data={this.props.platforms}
                                        placeholder="Plataformas para filtro..."
                                        value={this.state.filters.platform}
                                        style={{ width: '100%' }}
                                        onChange={(value) =>
                                            this.setState((prevState) => ({
                                                filters: {
                                                    ...prevState.filters,
                                                    platform: value,
                                                },
                                            }))
                                        }
                                    />
                                    <span className={styles.informationText}>
                                        *Se nenhum filtro for aplicado, todos os registros serão incluídos
                                    </span>
                                </div>
                            )}

                            <div className={styles.filterDiv} style={{ width: '100%' }}>
                                <span>Lançamentos</span>
                                <CheckPickerSelectFilter
                                    onSelectFilter={(values) => {
                                        this.setState((prevState) => ({
                                            filters: {
                                                ...prevState.filters,
                                                errors: values,
                                            },
                                        }))
                                    }}
                                    data={this.props.errors}
                                    placeholder="Lançamentos para filtro..."
                                    style={{ width: '100%' }}
                                    value={this.state.filters.errors}
                                    onChange={(value) =>
                                        this.setState((prevState) => ({
                                            filters: {
                                                ...prevState.filters,
                                                errors: value,
                                            },
                                        }))
                                    }
                                />
                                <span className={styles.informationText}>
                                    *Se nenhum filtro for aplicado, todos os registros serão incluídos
                                </span>
                            </div>
                        </div>

                        <div className={styles.filterDiv}>
                            <span>‎ </span>
                            <div className={styles.filterButtons}>
                                <Button
                                    appearance="primary"
                                    color="blue"
                                    loading={this.state.loadingButton}
                                    onClick={this.handleGetChartData}
                                    style={{ width: '200px' }}
                                >
                                    Filtrar
                                </Button>
                            </div>
                        </div>
                    </div>

                    <div className={styles.lineChartContainer}>
                        <div style={{ width: '100%' }}>
                            <div id="chart">
                                <ReactApexChart options={this.state.options} series={this.state.series} type="line" height={350} />
                            </div>
                            <div id="html-dist"></div>
                        </div>
                        <div className={styles.checkboxContainer}>
                            <Checkbox
                                indeterminate={this.state.filters.weekSelect.indeterminate}
                                checked={this.state.filters.weekSelect.checkAll}
                                onChange={this.handleCheckAll}
                            >
                                Marcar tudo
                            </Checkbox>
                            <CheckboxGroup
                                name="checkboxList"
                                value={this.state.filters.weekSelect.value}
                                onChange={this.handleChange}
                            >
                                {renderWeeks()}
                            </CheckboxGroup>
                        </div>
                    </div>
                </div>
            </>
        )
    }
}

export default LineChartPerWeek;
